import React from 'react'
import LanguageIcon from '@material-ui/icons/Language'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core'
import Button from '../Button'
import PopupMenu from '../PopupMenu'
import PopupMenuItem from '../PopupMenuItem'
import { getEnabledTranslationLangs, LANGUAGE_LABELS } from '../../providers/TranslationProvider'

const EN = 'en'

const useStyles = makeStyles((theme: Theme) => ({
  trigger: {
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.md,
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    padding: theme.spacing(0.5, 1)
  }
}))

export interface TranslationLanguageMenuProps {
  /** Currently selected language code (e.g. 'en', 'es', 'fr'). Defaults to 'en'. */
  currentLang?: string
  /** Enabled non-English languages (the raw `translations` flag value or availableLangs). */
  availableLangs?: string[]
  /** Called with the chosen language code ('en' for English). */
  onChange: (lang: string) => void
  disabled?: boolean
  className?: string
}

/**
 * Globe + current-language + caret dropdown for switching the on-screen translation
 * of a work order. Shared by the in-app WorkOrderViewDialog (from a deal) and the
 * customer-view presentation nav (from a link) so both surfaces look identical.
 */
export default function TranslationLanguageMenu({
  currentLang = EN,
  availableLangs,
  onChange,
  disabled,
  className
}: TranslationLanguageMenuProps) {
  const classes = useStyles()
  const enabledLangs = getEnabledTranslationLangs(availableLangs)
  const langs = [EN, ...enabledLangs.filter((lang) => lang !== EN)]
  const currentLabel = LANGUAGE_LABELS[currentLang] ?? LANGUAGE_LABELS[EN]

  return (
    <PopupMenu
      disabled={disabled}
      className={className}
      component={
        <Button
          variant="text"
          className={classes.trigger}
          disabled={disabled}
          startIcon={<LanguageIcon />}
          endIcon={<ArrowDropDownIcon />}
          aria-label="Select language"
        >
          {currentLabel}
        </Button>
      }
    >
      {langs.map((lang) => (
        <PopupMenuItem key={lang} selected={lang === currentLang} onClick={() => onChange(lang)}>
          {LANGUAGE_LABELS[lang] ?? lang}
        </PopupMenuItem>
      ))}
    </PopupMenu>
  )
}
